import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useOnSuccessCacheEntriesById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { dedupeSystemDocumentsByDocumentId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGeckoNotifications } from 'features/notifications'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexEntries,
  useRolodexHistogram,
} from 'features/rolodex'
import React, { createContext, useContext, useMemo } from 'react'
import {
  RolodexEntryType,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
} from 'types'

export type DocumentMetadataUpdate = {
  title?: string
  system?: string
  vendor_name?: string
  document_type?: string
  drawing_number?: string
  description?: string
  revision_code?: string
  revision_date?: string
}

interface KnowledgeDocumentDataContextType {
  systemDocumentEntries: SystemDocumentEntry[]
  updateDocumentMetadata: (
    documentId: string,
    metadata: DocumentMetadataUpdate
  ) => Promise<void>
  orgID: string
  siteID: string
  showDocumentOcrBoxes: boolean
  isLoadingDocuments: boolean
  uniqueAssetHistogram?: RolodexHistogramResponse[]
  allAssetHistogram?: RolodexHistogramResponse[]
}

interface KnowledgeDocumentDataProviderProps {
  children: React.ReactNode
  siteID: string
  orgID: string
}

export const KnowledgeDocumentDataProvider: React.FC<
  KnowledgeDocumentDataProviderProps
> = ({ children, siteID, orgID }) => {
  const { rolodexPatch } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    pipelineConfigCacheScope,
  } = usePipelineConfig()
  const onSuccessCacheEntriesById =
    useOnSuccessCacheEntriesById<SystemDocumentEntry>(pipelineConfigCacheScope)

  const siteConfigQuery = useMemo(
    () => ({
      type: [RolodexEntryType.SITE_CONFIG],
      tags: { site: siteID },
      limit: 1,
    }),
    [siteID]
  )
  const { siteConfigs } = useRolodexEntries(siteConfigQuery)
  const showDocumentOcrBoxes =
    siteConfigs[0]?.data.showDocumentOcrBoxes ?? false

  const systemDocumentLoadOptions = useMemo(
    () =>
      isPipelineConfigScopeLoading
        ? undefined
        : {
            type: [RolodexEntryType.SYSTEM_DOCUMENT],
            tags: getPipelineConfigScopedTags({ site: siteID }),
            limit: 1000000,
          },
    [getPipelineConfigScopedTags, isPipelineConfigScopeLoading, siteID]
  )
  const {
    data: systemDocumentEntries,
    mutate: refreshSystemDocuments,
    isLoading: isLoadingDocuments,
  } = useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
    systemDocumentLoadOptions,
    { onSuccess: onSuccessCacheEntriesById }
  )

  // Units are pipeline-config-agnostic; a config tag here matches nothing.
  const uniqueAssetHistogramQuery = useMemo<RolodexHistogramQuery | undefined>(
    () =>
      ({
        type: RolodexEntryType.UNIT,
        select: [
          {
            field: 'data',
            key: 'custom_fields.0.inputValue',
            label: 'asset_type',
            group_by: true,
          },
          {
            op: 'count',
            field: 'id',
          },
        ],
        tags: { site: siteID },
      }) as RolodexHistogramQuery,
    [siteID]
  )
  const { data: uniqueAssetHistogramData } = useRolodexHistogram(
    uniqueAssetHistogramQuery
  )

  const allAssetHistogramQuery = useMemo<RolodexHistogramQuery | undefined>(
    () =>
      isPipelineConfigScopeLoading
        ? undefined
        : ({
            type: RolodexEntryType.KNOWLEDGE_DOCUMENT,
            links: [RolodexEntryType.UNIT],
            select: [
              {
                type: RolodexEntryType.UNIT,
                field: 'data',
                key: 'custom_fields.0.inputValue',
                label: 'asset_type',
                group_by: true,
              },
              {
                op: 'count',
                field: 'id',
              },
            ],
            tags: getPipelineConfigScopedTags({
              site: siteID,
            }),
          } as RolodexHistogramQuery),
    [getPipelineConfigScopedTags, isPipelineConfigScopeLoading, siteID]
  )
  const { data: allAssetHistogramData } = useRolodexHistogram(
    allAssetHistogramQuery
  )

  const uniqueAssetHistogram = useMemo(() => {
    return uniqueAssetHistogramData
  }, [uniqueAssetHistogramData])

  const allAssetHistogram = useMemo(() => {
    return allAssetHistogramData
  }, [allAssetHistogramData])

  const updateDocumentMetadata = async (
    documentId: string,
    metadata: DocumentMetadataUpdate
  ) => {
    const documentEntry = systemDocumentEntries?.find(
      (entry) => entry.entry.id === documentId
    )

    if (!documentEntry) {
      errorNotification('Document not found')
      return
    }

    try {
      await rolodexPatch([
        {
          id: documentId,
          data: {
            ...documentEntry.entry.data,
            ...metadata,
          },
        },
      ])
      await refreshSystemDocuments()
      successNotification('Document metadata updated')
    } catch (error) {
      console.error('Error updating document metadata:', error)
      errorNotification('Failed to update document metadata')
      throw error
    }
  }

  // When no active/preview pipeline-config is selected, site-only loads can
  // return one system-document per config for the same BQ document_id.
  const dedupedSystemDocumentEntries = useMemo(
    () => dedupeSystemDocumentsByDocumentId(systemDocumentEntries ?? []),
    [systemDocumentEntries]
  )

  const contextValue: KnowledgeDocumentDataContextType = {
    systemDocumentEntries: dedupedSystemDocumentEntries,
    updateDocumentMetadata,
    orgID,
    siteID,
    showDocumentOcrBoxes,
    isLoadingDocuments,
    uniqueAssetHistogram,
    allAssetHistogram,
  }

  return (
    <KnowledgeDocumentDataContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentDataContext.Provider>
  )
}

const KnowledgeDocumentDataContext = createContext<
  KnowledgeDocumentDataContextType | undefined
>(undefined)

export const useKnowledgeDocumentDataContext =
  (): KnowledgeDocumentDataContextType => {
    const context = useContext(KnowledgeDocumentDataContext)
    if (context === undefined) {
      throw new Error(
        'useKnowledgeDocumentDataContext must be used within a KnowledgeDocumentDataProvider'
      )
    }
    return context
  }
